import { useMemo, useState } from 'react'
import { format } from 'date-fns'
import { pl } from 'date-fns/locale'
import { toast } from 'sonner'
import { AlertTriangle, Clock } from 'lucide-react'

import { AUTO_CLOSE_LABEL } from '@czas/shared'

import Button from '@/components/ui/Button'
import Label from '@/components/ui/Label'
import { apiErrorMessage } from '@/lib/api'
import { cn } from '@/lib/cn'

import { useResolveAutoClose, type WorkSession } from './use-work-sessions'

function timeValue(date: Date): string {
  return `${String(date.getHours()).padStart(2, '0')}:${String(date.getMinutes()).padStart(2, '0')}`
}

/**
 * Modal „praca bez godziny zakończenia".
 *
 * Sesja została domknięta automatycznie (zwykle o 21:30, ale backend potrafi
 * uciąć ją wcześniej) — pracownik podaje właściwą godzinę wyjścia albo
 * potwierdza automatyczną. Przy korekcie backend dopisuje audit log i wysyła
 * push do adminów.
 */
export default function AutoCloseModal({
  session,
  onResolved,
  onDismiss,
}: {
  session: WorkSession
  /** Zapisano — lista odświeży się sama i pokaże ewentualną kolejną sesję. */
  onResolved: () => void
  /** „Zrobię to później" — modal znika, przypomnienie zostaje na ekranie. */
  onDismiss: () => void
}) {
  const resolve = useResolveAutoClose()

  const startedAt = useMemo(() => new Date(session.startedAt), [session.startedAt])
  const autoEndedAt = useMemo(
    () => new Date(session.autoClosedEndedAt ?? session.endedAt ?? session.startedAt),
    [session.autoClosedEndedAt, session.endedAt, session.startedAt],
  )
  // Godzina, którą faktycznie nadał backend — stała 21:30 kłamałaby przy
  // sesji uciętej wcześniej, a to ona jest górną granicą korekty.
  const autoCloseLabel = session.autoClosedEndedAt
    ? format(new Date(session.autoClosedEndedAt), 'HH:mm')
    : AUTO_CLOSE_LABEL

  const [value, setValue] = useState(() => timeValue(autoEndedAt))
  const [error, setError] = useState<string | null>(null)

  function correctedDate(): Date | null {
    const parts = value.split(':')
    const h = Number(parts[0])
    const m = Number(parts[1])
    if (!Number.isInteger(h) || !Number.isInteger(m)) return null
    // Kotwicą jest data ROZPOCZĘCIA, a doba przeskakuje dopiero gdy wpisana
    // godzina jest wcześniejsza niż start. Kotwiczenie na dacie auto-zamknięcia
    // psuło sesje nocne: dla startu 22:30 (cutoff następnego dnia) wyjście
    // o 23:15 lądowało dobę za późno i było odrzucane jako „po 21:30".
    const out = new Date(startedAt)
    out.setHours(h, m, 0, 0)
    if (out.getTime() <= startedAt.getTime()) {
      out.setDate(out.getDate() + 1)
    }
    return out
  }

  async function submit(endedAt?: string) {
    setError(null)
    try {
      await resolve.mutateAsync({ sessionId: session.id, endedAt })
      toast.success('Dziękujemy — godzina wyjścia zapisana')
      onResolved()
    } catch (err) {
      toast.error(apiErrorMessage(err))
    }
  }

  function handleSave() {
    const corrected = correctedDate()
    if (!corrected) {
      setError('Podaj godzinę w formacie GG:MM.')
      return
    }
    if (corrected.getTime() <= startedAt.getTime()) {
      setError(
        `Godzina wyjścia musi być późniejsza niż rozpoczęcie (${format(startedAt, 'HH:mm')}).`,
      )
      return
    }
    if (corrected.getTime() > autoEndedAt.getTime()) {
      setError(
        `Godzina wyjścia nie może być późniejsza niż ${autoCloseLabel}. Jeśli praca trwała dłużej — zgłoś to administratorowi.`,
      )
      return
    }
    void submit(corrected.toISOString())
  }

  const workedMinutes = (() => {
    const corrected = correctedDate()
    if (!corrected) return null
    const minutes = Math.round(
      (corrected.getTime() - startedAt.getTime()) / 60_000,
    )
    return minutes > 0 ? minutes : null
  })()

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="auto-close-title"
      className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-bg-canvas/85 backdrop-blur-sm p-4"
    >
      <div className="rounded-card border border-border-subtle bg-bg-elevated p-6 max-w-md w-full">
        <div className="flex items-start gap-3 mb-3">
          <AlertTriangle className="h-5 w-5 text-warning flex-shrink-0 mt-0.5" aria-hidden />
          <div>
            <h2 id="auto-close-title" className="text-lg font-semibold">
              Praca nie została zakończona
            </h2>
            <p className="text-sm text-text-muted mt-1">
              {format(startedAt, 'EEEE, d MMMM', { locale: pl })} — praca od{' '}
              <span className="font-mono tabular-nums text-text-primary">
                {format(startedAt, 'HH:mm')}
              </span>
              . System zamknął sesję automatycznie o{' '}
              <span className="font-mono tabular-nums text-text-primary">
                {autoCloseLabel}
              </span>
              .
            </p>
          </div>
        </div>

        <p className="text-sm text-text-muted mb-4">
          Podaj godzinę faktycznego wyjścia — skorygujemy sesję i
          poinformujemy administratora.
        </p>

        <div className="mb-4">
          <Label htmlFor="auto-close-time">Godzina wyjścia</Label>
          <div className="flex items-center gap-3">
            <input
              id="auto-close-time"
              type="time"
              value={value}
              onChange={(e) => {
                setValue(e.target.value)
                setError(null)
              }}
              className={cn(
                'h-12 rounded-action border bg-bg-elevated px-3 text-lg font-mono tabular-nums',
                'focus:outline-none focus:ring-2 focus:ring-brand-accent/40',
                error ? 'border-danger/60' : 'border-border-strong',
              )}
            />
            {workedMinutes !== null && (
              <span className="inline-flex items-center gap-1.5 text-sm text-text-muted">
                <Clock className="h-3.5 w-3.5" aria-hidden />
                {Math.floor(workedMinutes / 60)}h {workedMinutes % 60}m pracy
              </span>
            )}
          </div>
          {error && <p className="mt-1.5 text-xs text-danger">{error}</p>}
        </div>

        <div className="flex flex-col gap-2">
          <Button type="button" onClick={handleSave} loading={resolve.isPending}>
            Zapisz godzinę wyjścia
          </Button>
          <Button
            type="button"
            variant="secondary"
            disabled={resolve.isPending}
            onClick={() => void submit()}
          >
            Praca do {autoCloseLabel} — potwierdzam
          </Button>
          <button
            type="button"
            onClick={onDismiss}
            disabled={resolve.isPending}
            className="text-xs text-text-faint hover:text-text-muted transition py-2 disabled:opacity-50"
          >
            Zrobię to później
          </button>
        </div>
      </div>
    </div>
  )
}

/**
 * Przypomnienie na ekranie głównym po odłożeniu modala — żeby sesja bez
 * potwierdzonej godziny nie zniknęła pracownikowi z oczu.
 *
 * Treść leci `text-text-primary`, a `warning` zostaje ikonie i obramowaniu:
 * para warning-na-warning/10 daje w JASNYM motywie ok. 2,7:1 (AA wymaga 4.5:1),
 * choć w ciemnym wychodzi ok. 5,4:1 — usterka widoczna w jednym trybie z dwóch.
 */
export function AutoCloseReminder({
  count,
  onOpen,
}: {
  count: number
  onOpen: () => void
}) {
  return (
    <button
      type="button"
      onClick={onOpen}
      className="w-full text-left rounded-card border border-warning/40 bg-warning/10 px-4 py-3 flex items-center gap-3 hover:bg-warning/15 transition"
    >
      <AlertTriangle className="h-4 w-4 text-warning flex-shrink-0" aria-hidden />
      <span className="text-sm text-text-primary">
        {count === 1
          ? 'Jedna sesja czeka na podanie godziny wyjścia.'
          : `${count} sesje czekają na podanie godziny wyjścia.`}{' '}
        <span className="underline underline-offset-2">Uzupełnij</span>
      </span>
    </button>
  )
}
